import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Search, Filter, Send, Users, Phone, Mail, MessageSquare, 
  Radio, Newspaper, Video, X, ChevronDown, ChevronRight,
  Plus, Trash2, Save, RefreshCw, Database, Zap, User
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { ScrollArea } from "./components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Type icons mapping
const TYPE_ICONS = {
  radio_flashes: Radio,
  social_posts: MessageSquare,
  phone_calls: Phone,
  emails: Mail,
  articles: Newspaper,
  animator_actions: Users,
  sms: MessageSquare,
};

const TYPE_COLORS = {
  radio_flashes: "bg-red-600",
  social_posts: "bg-blue-600",
  phone_calls: "bg-amber-600",
  emails: "bg-green-600",
  articles: "bg-slate-600",
  animator_actions: "bg-orange-600",
  sms: "bg-purple-600",
};

const TYPE_LABELS = {
  radio_flashes: "Flash Radio/TV",
  social_posts: "Réseaux Sociaux",
  phone_calls: "Appels",
  emails: "Emails",
  articles: "Articles/Dépêches",
  animator_actions: "Actions Animateur",
  sms: "SMS/WhatsApp",
};

// ============================================================================
// INPUTS BANK BROWSER COMPONENT
// ============================================================================
export const InputsBankBrowser = ({ onInjectInput, sessionId }) => {
  const [inputs, setInputs] = useState({});
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedCrisis, setSelectedCrisis] = useState("all");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [crisisTypes, setCrisisTypes] = useState({});

  // Load inputs bank
  useEffect(() => {
    const loadBank = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/inputs/bank`);
        setInputs(res.data.inputs || {});
        setCounts(res.data.counts || {});
        setCrisisTypes(res.data.crisis_types || {});
      } catch (err) {
        console.error("Error loading inputs bank:", err);
        toast.error("Erreur chargement banque d'inputs");
      } finally {
        setLoading(false);
      }
    };
    loadBank();
  }, []);

  // Filter inputs
  const getFilteredInputs = useCallback((category, items) => {
    if (!items) return [];
    
    return items.filter(item => {
      // Filter by type
      if (selectedType !== "all" && category !== selectedType) return false;
      
      // Filter by crisis type
      if (selectedCrisis !== "all" && item.crisis_type !== selectedCrisis && item.crisis_type !== "all") return false;
      
      // Filter by search term
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const content = (item.content || item.title || item.script || "").toLowerCase();
        const source = (item.source || "").toLowerCase();
        return content.includes(search) || source.includes(search);
      }
      
      return true;
    });
  }, [selectedType, selectedCrisis, searchTerm]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleInject = (input, category) => {
    if (onInjectInput) {
      onInjectInput({
        ...input,
        input_type: category
      });
      toast.success(`Input "${input.title || input.content?.substring(0, 30)}..." injecté`);
    }
  };

  const totalFiltered = Object.entries(inputs).reduce((sum, [cat, items]) => {
    return sum + getFilteredInputs(cat, items).length;
  }, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" />
        Chargement de la banque...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header & Stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-destructive" />
          <span className="font-bold">BANQUE D'INPUTS</span>
          <Badge className="bg-destructive">{Object.values(counts).reduce((a, b) => a + b, 0)} total</Badge>
        </div>
        <Badge variant="outline">{totalFiltered} filtré(s)</Badge>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 input-matrix"
          />
        </div>
        
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="bg-secondary border border-border rounded px-3 py-2 text-sm"
        >
          <option value="all">📋 Tous les types</option>
          {Object.keys(counts).map(type => (
            <option key={type} value={type}>
              {TYPE_LABELS[type] || type} ({counts[type]})
            </option>
          ))}
        </select>
        
        <select
          value={selectedCrisis}
          onChange={(e) => setSelectedCrisis(e.target.value)}
          className="bg-secondary border border-border rounded px-3 py-2 text-sm"
        >
          <option value="all">🎯 Toutes les crises</option>
          {Object.entries(crisisTypes).map(([key, info]) => (
            <option key={key} value={key}>
              {info.icon} {info.label}
            </option>
          ))}
        </select>
      </div>

      {/* Categories & Inputs */}
      <ScrollArea className="h-[500px]">
        <div className="space-y-2">
          {Object.entries(inputs).map(([category, items]) => {
            const filteredItems = getFilteredInputs(category, items);
            if (filteredItems.length === 0) return null;
            
            const Icon = TYPE_ICONS[category] || Database;
            const isExpanded = expandedCategories[category];
            
            return (
              <div key={category} className="border border-border rounded">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category)}
                  className={`w-full flex items-center justify-between p-3 ${TYPE_COLORS[category]} text-white rounded-t`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <span className="font-bold">{TYPE_LABELS[category] || category}</span>
                    <Badge className="bg-white/20">{filteredItems.length}</Badge>
                  </div>
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                
                {/* Items */}
                {isExpanded && (
                  <div className="p-2 space-y-1 max-h-[300px] overflow-y-auto bg-secondary/20">
                    {filteredItems.map((item, idx) => (
                      <div
                        key={item.id || idx}
                        className="p-2 bg-background rounded border border-border hover:border-destructive/50 transition-colors group"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {item.source && (
                                <Badge variant="outline" className="text-xs">
                                  {item.source}
                                </Badge>
                              )}
                              {item.crisis_type && item.crisis_type !== "all" && (
                                <Badge className="text-xs bg-secondary">
                                  {item.crisis_type}
                                </Badge>
                              )}
                              {item.phase && (
                                <Badge className="text-xs bg-amber-600/50">
                                  {item.phase}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium truncate">
                              {item.title || item.caller_role || item.role || "Sans titre"}
                            </p>
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {item.content || item.script || item.action || ""}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 bg-destructive hover:bg-destructive/80"
                            onClick={() => handleInject(item, category)}
                          >
                            <Zap className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

// ============================================================================
// PLAYER CONFIGURATION COMPONENT
// ============================================================================
export const PlayerConfiguration = ({ sessionId, onConfigSaved }) => {
  const [roles, setRoles] = useState([]);
  const [players, setPlayers] = useState([]);
  const [localMedia, setLocalMedia] = useState([]);
  const [newMediaName, setNewMediaName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load standard roles and existing config
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load standard roles
        const rolesRes = await axios.get(`${API}/roles/standard`);
        setRoles(rolesRes.data.roles || []);
        
        // Initialize players from roles (all inactive by default except default_active)
        const initialPlayers = rolesRes.data.roles.map(role => ({
          role_id: role.id,
          role_name: role.name,
          role_title: role.title,
          name: "",
          email: "",
          phone: "",
          is_active: role.default_active || false
        }));
        setPlayers(initialPlayers);
        
        // Try to load existing config if sessionId provided
        if (sessionId) {
          try {
            const configRes = await axios.get(`${API}/sessions/${sessionId}/players`);
            if (configRes.data.players?.length > 0) {
              setPlayers(prev => prev.map(p => {
                const existing = configRes.data.players.find(ep => ep.role_id === p.role_id);
                return existing ? { ...p, ...existing } : p;
              }));
            }
            if (configRes.data.local_media) {
              setLocalMedia(configRes.data.local_media);
            }
          } catch (e) {
            // Session might not exist yet, ignore
          }
        }
      } catch (err) {
        console.error("Error loading roles:", err);
        toast.error("Erreur chargement des rôles");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [sessionId]);

  const updatePlayer = (roleId, field, value) => {
    setPlayers(prev => prev.map(p => 
      p.role_id === roleId ? { ...p, [field]: value } : p
    ));
  };

  const togglePlayerActive = (roleId) => {
    setPlayers(prev => prev.map(p => 
      p.role_id === roleId ? { ...p, is_active: !p.is_active } : p
    ));
  };

  const addLocalMedia = () => {
    if (newMediaName.trim() && !localMedia.includes(newMediaName.trim())) {
      setLocalMedia(prev => [...prev, newMediaName.trim()]);
      setNewMediaName("");
    }
  };

  const removeLocalMedia = (name) => {
    setLocalMedia(prev => prev.filter(m => m !== name));
  };

  const saveConfiguration = async () => {
    if (!sessionId) {
      toast.error("Aucune session sélectionnée");
      return;
    }
    
    try {
      setSaving(true);
      await axios.post(`${API}/sessions/${sessionId}/configure-players`, {
        players: players.filter(p => p.is_active),
        local_media: localMedia
      });
      toast.success("Configuration sauvegardée !");
      if (onConfigSaved) onConfigSaved(players, localMedia);
    } catch (err) {
      console.error("Error saving config:", err);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" />
        Chargement...
      </div>
    );
  }

  const activePlayers = players.filter(p => p.is_active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-destructive" />
          <span className="font-bold">CONFIGURATION DES JOUEURS</span>
          <Badge className="bg-green-600">{activePlayers} actif(s)</Badge>
        </div>
        <Button 
          onClick={saveConfiguration} 
          disabled={saving}
          className="bg-green-600 hover:bg-green-700"
        >
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Sauvegarder
        </Button>
      </div>

      {/* Players Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {players.map(player => {
          const role = roles.find(r => r.id === player.role_id);
          
          return (
            <div 
              key={player.role_id}
              className={`p-4 rounded-lg border transition-all ${
                player.is_active 
                  ? 'border-green-500 bg-green-900/20' 
                  : 'border-border bg-secondary/30 opacity-60'
              }`}
            >
              {/* Role Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{role?.icon || "👤"}</span>
                  <div>
                    <p className="font-bold text-sm">{role?.name}</p>
                    <p className="text-xs text-muted-foreground">{role?.title}</p>
                  </div>
                </div>
                <button
                  onClick={() => togglePlayerActive(player.role_id)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    player.is_active ? 'bg-green-600' : 'bg-gray-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    player.is_active ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              {/* Player Info Fields */}
              {player.is_active && (
                <div className="space-y-2">
                  <Input
                    placeholder="Nom du joueur"
                    value={player.name}
                    onChange={(e) => updatePlayer(player.role_id, 'name', e.target.value)}
                    className="input-matrix text-sm"
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={player.email}
                    onChange={(e) => updatePlayer(player.role_id, 'email', e.target.value)}
                    className="input-matrix text-sm"
                  />
                  <Input
                    placeholder="Téléphone (pour appels animateur)"
                    value={player.phone}
                    onChange={(e) => updatePlayer(player.role_id, 'phone', e.target.value)}
                    className="input-matrix text-sm"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Local Media Configuration */}
      <div className="border border-border rounded-lg p-4">
        <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
          <Newspaper className="w-4 h-4" /> MÉDIAS LOCAUX PERSONNALISÉS
        </h4>
        <p className="text-xs text-muted-foreground mb-3">
          Ajoutez des noms de médias locaux fictifs qui apparaîtront dans les articles générés.
        </p>
        
        <div className="flex gap-2 mb-3">
          <Input
            placeholder="Ex: Lyon Mag, Info Rhône..."
            value={newMediaName}
            onChange={(e) => setNewMediaName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addLocalMedia()}
            className="input-matrix"
          />
          <Button onClick={addLocalMedia} size="sm">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {localMedia.map(name => (
            <Badge key={name} variant="secondary" className="flex items-center gap-1">
              {name}
              <button onClick={() => removeLocalMedia(name)} className="hover:text-destructive">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
          {localMedia.length === 0 && (
            <span className="text-xs text-muted-foreground">Aucun média local ajouté</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// INPUTS GENERATOR COMPONENT
// ============================================================================
export const InputsGenerator = ({ onInputsGenerated }) => {
  const [config, setConfig] = useState({
    crisis_type: "incendie",
    company_name: "",
    city: "",
    event_description: "",
    local_media: [],
    social_count: 50,
    email_count_per_role: 10,
    sms_count_per_role: 5,
    call_count_per_role: 3
  });
  const [generating, setGenerating] = useState(false);
  const [generatedInputs, setGeneratedInputs] = useState(null);

  const handleGenerate = async () => {
    if (!config.company_name || !config.city || !config.event_description) {
      toast.error("Remplissez tous les champs obligatoires");
      return;
    }
    
    try {
      setGenerating(true);
      const res = await axios.post(`${API}/inputs/generate`, config);
      setGeneratedInputs(res.data);
      toast.success(`${res.data.stats.total} inputs générés !`);
      if (onInputsGenerated) onInputsGenerated(res.data.inputs);
    } catch (err) {
      console.error("Error generating inputs:", err);
      toast.error("Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Zap className="w-5 h-5 text-amber-500" />
        <span className="font-bold">GÉNÉRATEUR D'INPUTS</span>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Type de crise *</label>
            <select
              value={config.crisis_type}
              onChange={(e) => setConfig({...config, crisis_type: e.target.value})}
              className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm mt-1"
            >
              <option value="incendie">🔥 Incendie Industriel</option>
              <option value="cyberattaque">💻 Cyberattaque</option>
              <option value="produit">📦 Crise Produit</option>
              <option value="maritime">🚢 Crise Maritime</option>
              <option value="sociale">✊ Crise Sociale</option>
              <option value="environnement">🌍 Crise Environnementale</option>
            </select>
          </div>
          
          <div>
            <label className="text-xs text-muted-foreground">Nom de l'entreprise *</label>
            <Input
              value={config.company_name}
              onChange={(e) => setConfig({...config, company_name: e.target.value})}
              placeholder="Ex: ChimieIndustrie"
              className="input-matrix mt-1"
            />
          </div>
          
          <div>
            <label className="text-xs text-muted-foreground">Ville *</label>
            <Input
              value={config.city}
              onChange={(e) => setConfig({...config, city: e.target.value})}
              placeholder="Ex: Rouen"
              className="input-matrix mt-1"
            />
          </div>
        </div>
        
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Description de l'événement *</label>
            <textarea
              value={config.event_description}
              onChange={(e) => setConfig({...config, event_description: e.target.value})}
              placeholder="Décrivez brièvement la situation de crise..."
              className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm mt-1 h-24 resize-none"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-muted-foreground">Posts sociaux</label>
              <Input
                type="number"
                value={config.social_count}
                onChange={(e) => setConfig({...config, social_count: parseInt(e.target.value) || 0})}
                className="input-matrix mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Appels/rôle</label>
              <Input
                type="number"
                value={config.call_count_per_role}
                onChange={(e) => setConfig({...config, call_count_per_role: parseInt(e.target.value) || 0})}
                className="input-matrix mt-1"
              />
            </div>
          </div>
        </div>
      </div>
      
      <Button 
        onClick={handleGenerate} 
        disabled={generating}
        className="w-full bg-amber-600 hover:bg-amber-700"
      >
        {generating ? (
          <>
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            Génération en cours...
          </>
        ) : (
          <>
            <Zap className="w-4 h-4 mr-2" />
            Générer les inputs
          </>
        )}
      </Button>
      
      {/* Results */}
      {generatedInputs && (
        <div className="p-4 bg-green-900/20 border border-green-500 rounded-lg">
          <h4 className="font-bold text-green-400 mb-2">✅ Génération réussie !</h4>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div>📱 {generatedInputs.stats.social_posts} posts</div>
            <div>📰 {generatedInputs.stats.articles} articles</div>
            <div>📧 {generatedInputs.stats.emails} emails</div>
            <div>💬 {generatedInputs.stats.sms} SMS</div>
            <div>📞 {generatedInputs.stats.phone_calls} appels</div>
            <div className="font-bold">Total: {generatedInputs.stats.total}</div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ROLE-SPECIFIC DASHBOARD PREVIEW
// ============================================================================
export const RoleDashboardPreview = ({ roleId, roleName, sessionId }) => {
  const [inputs, setInputs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadInputs = async () => {
      if (!sessionId || !roleId) return;
      
      try {
        setLoading(true);
        const res = await axios.get(`${API}/sessions/${sessionId}/inputs-by-role/${roleId}`);
        setInputs([...res.data.messages, ...res.data.injected_inputs]);
      } catch (err) {
        console.error("Error loading role inputs:", err);
      } finally {
        setLoading(false);
      }
    };
    loadInputs();
  }, [sessionId, roleId]);

  if (loading) {
    return <div className="text-center p-4 text-muted-foreground">Chargement...</div>;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-3">
        <User className="w-4 h-4" />
        <span className="font-bold">{roleName}</span>
        <Badge>{inputs.length} inputs</Badge>
      </div>
      
      {inputs.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground text-sm">
          Aucun input pour ce rôle
        </div>
      ) : (
        <ScrollArea className="h-[200px]">
          {inputs.map((input, idx) => (
            <div key={idx} className="p-2 mb-1 bg-secondary/30 rounded text-sm">
              <Badge variant="outline" className="text-xs mb-1">{input.type || input.message_type}</Badge>
              <p className="text-xs truncate">{input.content || input.title}</p>
            </div>
          ))}
        </ScrollArea>
      )}
    </div>
  );
};

export default InputsBankBrowser;
